import { useTheme } from '@emotion/react'
import {
    AppState,
    AuthenticationState,
    ComponentServices,
    createCardClickedEvent,
    PublicationCardItem,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { useSelector } from 'react-redux'
import { FundingLabel } from '../../../advertising/FundingLabel/FundingLabel'
import { ResponsiveContainer } from '../../../content/Picture/responsive'
import { ResponsivePictureLayout } from '../../../content/Picture/ResponsivePictureLayouts'
import { VideoQueue } from '../../../templates/Publication/lib/get-video-queue'
import { ThemeOverrider } from '../../../themes/ThemeOverrider/ThemeOverrider'
import { isVideoClick } from '../../../__helpers/video-card-helpers'
import { ThemeOverrideTypes } from '../../../__styling/themes'
import { CardByline } from '../../CardByline/CardByline'
import { CommonCardProps } from '../../CardItem.Props'
import { CardText } from '../../CardText/CardText'
import {
    returnEntitledStatus,
    returnFundingTypeIfItemLoaded,
    returnImageWidthsFromContainerRatios,
    returnVideoQueue,
} from '../utils'
import {
    StyledContent,
    StyledHeroCard,
    StyledHeroCardLink,
    StyledHeroCardMedia,
    StyledMediaWrapper,
} from './HeroCard.styled'

export interface HeroCardProps extends CommonCardProps, ResponsiveContainer {
    item: MaybeLoaded<PublicationCardItem>
    cardNumber: number
    cardServices?: ComponentServices
    adUnitPath: string | undefined
    mediaPosition?: 'left' | 'right'
    isLarge?: boolean
    hideBorder?: boolean
    overrideRestrictedVideoPlayback?: boolean
    themeOverride?: ThemeOverrideTypes
    showPublicationDate?: boolean
}

export function HeroCard(props: HeroCardProps) {
    const {
        item,
        cardNumber,
        onEvent,
        containerWidthRatios,
        hideByline = false,
        cardServices,
        adUnitPath,
        mediaPosition = 'right',
        isLarge = true,
        hideBorder = false,
        overrideRestrictedVideoPlayback = false,
        showPublicationDate = false,
    } = props

    // isLarge determines the size of the play button on video cards, the underline on the card and the byline
    // isLarge = false && playButton = small, underline = hidden, byline=hidden
    const theme = useTheme()
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    const storyClassification = useFeature('story-classification')

    const fundingType = returnFundingTypeIfItemLoaded({
        item,
        storyClassification,
    })

    const imageWidths = returnImageWidthsFromContainerRatios({
        theme,
        containerWidthRatios,
    })

    const isEntitled = returnEntitledStatus({
        overrideRestrictedVideoPlayback,
        authentication,
    })

    const requiredAccessLevel = item.loaded && item.result.requiredAccess.level

    const isPremiumArticle =
        requiredAccessLevel === 'subscriber' ||
        requiredAccessLevel === 'registered'

    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!item.loaded) {
            e.preventDefault()
            return
        }

        if (isEntitled && isVideoClick(e.nativeEvent.target)) {
            e.preventDefault()
            return
        }

        onEvent(
            createCardClickedEvent(
                item.result,
                'InternalHeroCard',
                'HeroCard',
                cardNumber,
                fundingType,
            ),
        )
    }

    let getVideoQueue: Promise<VideoQueue>

    if (cardServices) {
        getVideoQueue = returnVideoQueue({ item, cardServices })
    }

    return (
        <ThemeOverrider override={props.themeOverride}>
            <StyledHeroCardLink
                to={toLinkWithHint(item)}
                onClick={linkClicked}
                isLoading={!item.loaded}
            >
                <StyledHeroCard
                    mediaPosition={mediaPosition}
                    isLarge={isLarge}
                    hideBorder={hideBorder}
                >
                    <StyledContent mediaPosition={mediaPosition}>
                        <CardText
                            item={item}
                            hasBackground={false}
                            kickerMode={{ size: 'large' }}
                            teaserMode={'visible'}
                            fontScale={1.75}
                            onEvent={onEvent}
                        />
                        {!hideByline && isLarge && (
                            <CardByline
                                item={item}
                                onEvent={onEvent}
                                showProfile={true}
                                showPublicationDate={showPublicationDate}
                            />
                        )}
                        {fundingType && (
                            <FundingLabel
                                hasBackground={false}
                                fundingType={fundingType}
                            />
                        )}
                    </StyledContent>
                    <StyledMediaWrapper
                        mediaPosition={mediaPosition ? mediaPosition : 'right'}
                    >
                        <StyledHeroCardMedia
                            isLarge={false}
                            item={item}
                            fixedRatio="16:9"
                            hasBackground={false}
                            imageWidths={imageWidths}
                            onEvent={onEvent}
                            mediaMode={'default'}
                            disableImageLazyLoad={true}
                            imageLayout={
                                ResponsivePictureLayout.ObjectFitContain
                            }
                            willPlayVideoInline={
                                isPremiumArticle ? isEntitled : true
                            }
                            getVideoQueue={() => getVideoQueue}
                            adUnitPath={adUnitPath}
                        />
                    </StyledMediaWrapper>
                </StyledHeroCard>
            </StyledHeroCardLink>
        </ThemeOverrider>
    )
}

HeroCard.displayName = 'HeroCard'
